import Chart from 'chart.js';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import moment, { Moment } from 'moment';
import { DefaultButton, Slider } from 'office-ui-fabric-react';
import React, { FC, useEffect, useState } from 'react';
import * as api from '../../../api/generated';
import * as components from '../../../components';
import { KendoLoading } from '../../../components';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import { INugisStatisticFilter } from './NugisStatistics';

interface IProps {
    timestamp: string | undefined;
    filter: INugisStatisticFilter;
}

export interface IStatisticTimesMonitorGraphsData {
    graphDataWaitTimes: IStatisticTimesMonitorGraphsDataItem[] | null;
}

interface IStatisticTimesMonitorGraphsDataItem {
    date: Moment;
    values: IMinutesCount[];
}

interface IMinutesCount {
    minutes: number;
    count: number;
}

const NugisStatisticsTimesMonitorGraphs: FC<IProps> = props => {
    const [error, setError] = useState<string | null>(null);

    const MAX_WAIT_TIME = 60;
    const [timesMonitorStatisticGraphsData, setTimesMonitorStatisticGraphsData] = useState<IStatisticTimesMonitorGraphsData | null>(null);

    const [timestamp, setTimestamp] = useState<string>();

    const [waitTimeUpperLimit, setwaitTimeUpperLimit] = useState<number>(5);
    const [waitTimeInterval, setWaitTimeInterval] = useState<number>(5);

    const fetchData = async () => {
        try {
            setTimesMonitorStatisticGraphsData(null);
            const resp = await api.statistic.getNugisStatisticsTimesMonitorGraphsData({
                ...props.filter,
                practitioners: props.filter.practitioners.map(x => x.text),
                dateFrom: moment(props.filter.dateFrom!),
                dateTo: moment(props.filter.dateTo!),
                timeFrom: props.filter.timeFrom!,
                timeTo: props.filter.timeTo!
            });
            setTimesMonitorStatisticGraphsData(resp);
        } catch (err) {
            setError(err.error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [props.timestamp]);

    const getWaitTimeChartDatasets = (): any => {
        var datasets: Chart.ChartDataSets[] = [];

        datasets.push({
            label: `0-${waitTimeUpperLimit} min`,
            backgroundColor: 'rgb(0,100,0)',
            data: Array(timesMonitorStatisticGraphsData!.graphDataWaitTimes!.length).fill(waitTimeUpperLimit),
            datalabels: {
                align: 'center',
                anchor: 'center',
                color: 'white',
                font: {
                    weight: 'bold'
                }
            }
        });

        let index = 1;
        let intervalsCount = (MAX_WAIT_TIME - waitTimeUpperLimit) / waitTimeInterval + 1;

        while (index < intervalsCount) {
            datasets.push({
                backgroundColor: `rgb(${255 - index * 15},0,0)`,
                data: Array(timesMonitorStatisticGraphsData!.graphDataWaitTimes!.length).fill(waitTimeInterval),
                datalabels: {
                    align: 'center',
                    anchor: 'center',
                    color: 'white',
                    font: {
                        weight: 'bold'
                    }
                }
            });

            index++;
        }

        return datasets;
    };

    var waitTimeChart: Chart;
    useEffect(() => {
        if (!timesMonitorStatisticGraphsData || !timesMonitorStatisticGraphsData.graphDataWaitTimes) return;

        if (waitTimeChart) {
            waitTimeChart.destroy();
        }

        var canvas: any = document.getElementById('waitTimeChart');
        if (canvas == null) return;

        var ctx = canvas.getContext('2d');
        waitTimeChart = new Chart(ctx, {
            plugins: [ChartDataLabels],
            type: 'bar',
            data: {
                labels: timesMonitorStatisticGraphsData?.graphDataWaitTimes?.map(x => x.date.format('YY/M/D')),
                datasets: getWaitTimeChartDatasets()
            },
            options: {
                events: [],
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    enabled: false
                },
                legend: {
                    display: false
                },
                scales: {
                    xAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Data'
                            },
                            stacked: true
                        }
                    ],
                    yAxes: [
                        {
                            scaleLabel: {
                                display: true,
                                labelString: 'Minutės'
                            },
                            ticks: {
                                beginAtZero: true,
                                stepSize: 1
                            },
                            stacked: true
                        }
                    ]
                },
                plugins: {
                    datalabels: {
                        formatter: (_value: any, context: Context) => {
                            var index = context.datasetIndex;
                            var lowerIntervalLimit = 0;
                            var upperIntervalLimit = 0;

                            if (index == 0) {
                                lowerIntervalLimit = 0;
                                upperIntervalLimit = waitTimeUpperLimit;
                            } else {
                                lowerIntervalLimit = waitTimeUpperLimit - waitTimeInterval + index * waitTimeInterval;
                                upperIntervalLimit = waitTimeUpperLimit - waitTimeInterval + (index + 1) * waitTimeInterval;
                            }

                            var sum = timesMonitorStatisticGraphsData!
                                .graphDataWaitTimes![context.dataIndex].values.filter(
                                    x => lowerIntervalLimit <= x.minutes && x.minutes < upperIntervalLimit
                                )
                                .reduce((sum, i) => sum + i.count, 0);
                            return sum;
                        }
                    }
                }
            }
        });
    }, [timesMonitorStatisticGraphsData?.graphDataWaitTimes, timestamp]);

    return (
        <>
            <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                        <Slider
                            label="Kritinis laukimo laikas (minutėmis)"
                            min={1}
                            max={30}
                            value={waitTimeUpperLimit}
                            showValue
                            onChange={value => setwaitTimeUpperLimit(value)}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm3 ms-md3 ms-lg3">
                        <Slider
                            label="Kritinio laukimo laiko intervalas (minutėmis)"
                            min={1}
                            max={30}
                            value={waitTimeInterval}
                            showValue
                            onChange={value => setWaitTimeInterval(value)}
                        />
                    </div>
                    <div className="ms-Grid-col ms-sm1 ms-md1 ms-lg1">
                        <DefaultButton
                            text="Atnaujinti"
                            onClick={() => {
                                setTimestamp(new Date().toString());
                            }}
                        />
                    </div>
                </div>
                <div className="ms-Grid-row">&nbsp;</div>
                {timesMonitorStatisticGraphsData && timesMonitorStatisticGraphsData.graphDataWaitTimes && (
                    <div className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                            <div style={{ overflow: 'scroll' }}>
                                <div
                                    className="chart-container"
                                    style={{
                                        position: 'relative',
                                        height: '1000px',
                                        width: `${timesMonitorStatisticGraphsData.graphDataWaitTimes.length * 50 + 50}px`
                                    }}
                                >
                                    <canvas id="waitTimeChart"></canvas>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {(!timesMonitorStatisticGraphsData || !timesMonitorStatisticGraphsData.graphDataWaitTimes) && <KendoLoading />}
            {error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
        </>
    );
};

export default NugisStatisticsTimesMonitorGraphs;
